import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, of, tap } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';
import {
  CryptoService,
  StorageProvider,
  StorageService,
} from '@ps/core-data-access';

import { parseJson } from '@ps/utils';

import { User } from '../../models/user.model';
import {
  PersistAuthSettings,
  PersistUserSettings,
} from '../../models/auth.model';
import { ImpersonatedBuyer } from '../../models/impersonation.model';

const PREFIX = 'ps-';

const KEYS = {
  user: `${PREFIX}user`,
  impersonatedBuyer: `${PREFIX}impersonated-buyer`,
} as const;

@Injectable({ providedIn: 'root' })
export class SecureAuthStorageService {
  private readonly _env = inject(ENV_CONFIG);
  private readonly _storage = inject(StorageService);
  private readonly _crypto = inject(CryptoService);

  public save({ user, rememberMe }: PersistUserSettings): Observable<void> {
    return this._save(KEYS.user, user, rememberMe);
  }

  public saveImpersonatedBuyer(data: ImpersonatedBuyer): Observable<void> {
    const rememberMe = this._storage.has(KEYS.user, 'persistent');
    return this._save(KEYS.impersonatedBuyer, data, rememberMe);
  }

  public load(): Observable<PersistAuthSettings | null> {
    return forkJoin([this._loadUser(), this._loadImpersonatedBuyer()]).pipe(
      map(([userSettings, impersonatedBuyer]) => {
        if (!userSettings) return null;

        return {
          ...userSettings,
          impersonatedBuyer,
        };
      }),
    );
  }

  public clearImpersonation(): void {
    this._storage.remove(KEYS.impersonatedBuyer);
  }

  public clear(): void {
    this._storage.clear();
  }

  private _save(
    key: string,
    data: User | ImpersonatedBuyer,
    rememberMe: boolean,
  ): Observable<void> {
    const salt = this._env.pureSpectrum.storageSalt;

    return this._crypto.encrypt(JSON.stringify(data), salt).pipe(
      tap((encrypted) =>
        this._storage.set(key, encrypted, this._provider(rememberMe)),
      ),
      map(() => void 0),
    );
  }

  private _loadUser(): Observable<PersistUserSettings | null> {
    return this._storage.get<string>(KEYS.user).match({
      some: (encryptedUser) =>
        this._decrypt<User>(encryptedUser).pipe(
          tap((user) => {
            if (!user) {
              this.clear();
            }
          }),
          map((user) => {
            if (!user) return null;

            return {
              user,
              rememberMe: this._storage.has(KEYS.user, 'persistent'),
            };
          }),
        ),
      none: () => of(null),
    });
  }

  private _loadImpersonatedBuyer(): Observable<ImpersonatedBuyer | null> {
    return this._storage.get<string>(KEYS.impersonatedBuyer).match({
      some: (encryptedBuyer) =>
        this._decrypt<ImpersonatedBuyer>(encryptedBuyer).pipe(
          tap((impersonatedBuyer) => {
            if (!impersonatedBuyer) {
              this.clearImpersonation();
            }
          }),
        ),
      none: () => of(null),
    });
  }

  private _decrypt<T>(encrypted: string): Observable<T | null> {
    const salt = this._env.pureSpectrum.storageSalt;
    return this._crypto.decrypt(encrypted, salt).pipe(
      map((result) =>
        result
          .ok()
          .map(parseJson<T>)
          .flatten()
          .unwrapOr(null),
      ),
    );
  }

  private _provider(rememberMe: boolean): StorageProvider {
    return rememberMe ? 'persistent' : 'session';
  }
}
